import {
  Component,
  inject,
  OnInit,
  signal,
  computed,
  LOCALE_ID,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';

import {
  UndeliveredReason,
  GetAllReasonsResponse,
} from '../../../../pages/admin-dashboard/undelivered-reasons/undelivered-reasons.model';
import { UndeliveredReasonsService } from '../../../../services/undelivered-reasons/undelivered-reasons.service';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { FilterItemSearch } from '../../../../shared/ui/shared-filters/filter-item-search/filter-item-search';
import { FilterDateComponent } from '../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSortingComponent } from '../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { Loading } from '../../../../utils/loading/loading';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { UndeliveredReasonsDeleteComponent } from '../undelivered-reasons-delete-component/undelivered-reasons-delete-component';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { ScopeService } from '../../../../services/scope/scope-service';
import { AddIcon } from '../../../../shared/ui/icons/add-icon/add-icon';

@Component({
  selector: 'app-undelivered-reasons-list-component',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    Tables,
    Pagination,
    FilterItemSearch,
    FilterDateComponent,
    FilterSortingComponent,
    Loading,
    ErrorData,
    UndeliveredReasonsDeleteComponent,
    HasScopeDirective,
    AddIcon
],
  templateUrl: './undelivered-reasons-list-component.html',
  styleUrl: './undelivered-reasons-list-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UndeliveredReasonsListComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private undeliveredReasonsService = inject(UndeliveredReasonsService);
  private locale = inject(LOCALE_ID);
  private cdr = inject(ChangeDetectorRef);
  private scopeService = inject(ScopeService);

  protected readonly SCOPES = SCOPES;

  // ================= Pagination State =================
  currentPage = signal(1);
  itemsPerPage = signal(25);

  // ================= Filter State =================
  reasonFilter = signal('');
  createdOnFrom = signal('');
  createdOnTo = signal('');
  modifiedOnFrom = signal('');
  modifiedOnTo = signal('');
  size = signal(25);

  orderBy = signal<string>('CREATION_DATE');
  orderDir = signal<'ASC' | 'DESC'>('DESC');

  appliedFilters = signal({
    reason: '',
    createdOnFrom: '',
    createdOnTo: '',
    modifiedOnFrom: '',
    modifiedOnTo: '',
    orderBy: 'CREATION_DATE',
    orderDir: 'DESC' as 'ASC' | 'DESC',
  });

  reasons = signal<UndeliveredReason[]>([]);
  totalItems = signal(0);

  loading = signal(false);
  errorGettingReasons = signal<string | null>(null);

  // ================= Delete Modal State =================
  showDeleteDialog = signal(false);
  showSuccessMessage = signal(false);
  successToastMessage = signal('');
  reasonToDelete = signal<UndeliveredReason | null>(null);

  // ================= Search Filter =================
  searchLabel = computed(() => (this.isArabic() ? 'بحث' : 'Search'));
  searchPlaceholder = computed(() =>
    this.isArabic() ? 'ابحث باسم السبب' : 'Search by reason name'
  );

  // ================= Date Filters =================
  createdOnFromLabel = computed(() => (this.isArabic() ? 'تاريخ الإنشاء من' : 'Created On From'));
  createdOnToLabel = computed(() => (this.isArabic() ? 'تاريخ الإنشاء إلى' : 'Created On To'));
  modifiedOnFromLabel = computed(() => (this.isArabic() ? 'آخر تعديل من' : 'Last Modified From'));
  modifiedOnToLabel = computed(() => (this.isArabic() ? 'آخر تعديل إلى' : 'Last Modified To'));

  isCreatedDateRangeInvalid = computed(() => {
    const from = this.createdOnFrom();
    const to = this.createdOnTo();
    if (!from || !to) return false;
    return new Date(from) > new Date(to);
  });

  isModifiedDateRangeInvalid = computed(() => {
    const from = this.modifiedOnFrom();
    const to = this.modifiedOnTo();
    if (!from || !to) return false;
    return new Date(from) > new Date(to);
  });

  // ================= Sort Options =================
  readonly sortOptionsConfig = [
    { value: 'TITLE', labelEn: 'Title', labelAr: 'العنوان' },
    { value: 'CREATION_DATE', labelEn: 'Created On', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
  ];

  sortOptions(): typeof this.sortOptionsConfig {
    return this.sortOptionsConfig;
  }

  // ================= Table Config =================
  headerItems = computed(() => [
    { key: 'index', label: '#' },
    { key: 'reasonDisplay', label: this.isArabic() ? 'العنوان' : 'Title' },
    { key: 'type', label: this.isArabic() ? 'النوع' : 'Type', type: 'status' },
    { key: 'createdOnBy', label: this.isArabic() ? 'تاريخ الإنشاء / بواسطة' : 'Created On / By' },
    { key: 'lastModifiedOnBy', label: this.isArabic() ? 'آخر تعديل / بواسطة' : 'Last Modified On / By' },
    { key: 'actions', type: 'actions', label: '' },
  ]);

  readonly rowActions: ActionInterface[] = [
    { id: 1, titleEn: 'Edit', titleAr: 'تعديل' },
    { id: 2, titleEn: 'Delete', titleAr: 'حذف' },
  ];

  currentActions(): ActionInterface[] {
    return this.rowActions.filter((action) => {
      if (action.id === 1) {
        return this.scopeService.hasAnyScope([SCOPES.UNDELIVERED_REASONS.UPDATE]);
      }
      if (action.id === 2) {
        return this.scopeService.hasAnyScope([SCOPES.UNDELIVERED_REASONS.DELETE]);
      }
      return true;
    });
  }

  tableRows = computed(() => {
    const page = this.currentPage();
    const perPage = this.itemsPerPage();
    return this.reasons().map((item, i) => {
      const isCustom = item.isEditable && !!item.createdBy;

      return {
        ...item,
        index: i + 1 + (page - 1) * perPage,
        reasonDisplay: this.isArabic() && item.titleAr ? item.titleAr : item.titleEn,
        type: {
          titleEn: isCustom ? 'Custom' : 'System-defined',
          titleAr: isCustom ? 'مخصص' : 'معرف من النظام',
        },
        createdOn: item.createdOn,
        createdBy: isCustom
          ? item.createdBy?.fullName ?? ''
          : this.isArabic() ? 'النظام' : 'System',
        lastModifiedOn: item.lastModifiedOn || null,
        lastModifiedBy: item.lastModifiedBy?.fullName ?? '',
        isEditable: isCustom,
      };
    });
  });

  // ================= Empty States =================
  hasSearchTerm = computed(() => !!this.appliedFilters().reason);
  hasDateFilters = computed(() => {
    const f = this.appliedFilters();
    return !!(f.createdOnFrom || f.createdOnTo || f.modifiedOnFrom || f.modifiedOnTo);
  });

  isFiltersApplied = computed(() => this.hasSearchTerm() || this.hasDateFilters());

  emptyFilterTitle = computed(() =>
    this.isArabic()
      ? 'لا توجد أسباب تطابق نتائج البحث أو الفلترة'
      : 'No undelivered reasons match your criteria.'
  );

  emptyContentTitle = computed(() =>
    this.isArabic() ? 'لا توجد أسباب عدم تسليم' : 'No Undelivered Reasons Found'
  );

  emptyContentDescription = computed(() =>
    this.isArabic()
      ? 'لم يتم إضافة أسباب عدم تسليم بعد.'
      : 'No undelivered reasons have been created yet.'
  );

  // ================= Actions & Logic =================
  toggleDropdown(event: any): void {}

  onTableAction(event: any): void {}

  onHandleAction(payload: { event: Event; actionId: number; entity: UndeliveredReason }): void {
    if (!payload || !payload.entity) return;

    switch (payload.actionId) {
      case 1:
        this.onEditReason(payload.entity);
        break;
      case 2:
        this.onDeleteReason(payload.entity);
        break;
    }
  }

  ngOnInit(): void {
    this.loadReasons();
  }

  isArabic(): boolean {
    return typeof this.locale === 'string' && this.locale.startsWith('ar');
  }

  loadReasons(): void {
    this.loading.set(true);
    this.errorGettingReasons.set(null);

    const f = this.appliedFilters();

    this.undeliveredReasonsService
      .getAllReasons({
        quickSearch: f.reason || undefined,
        createdOnFrom: f.createdOnFrom || undefined,
        createdOnTo: f.createdOnTo || undefined,
        lastModifiedOnFrom: f.modifiedOnFrom || undefined,
        lastModifiedOnTo: f.modifiedOnTo || undefined,
        pageSize: this.itemsPerPage(),
        pageNum: this.currentPage() - 1,
        orderBy: f.orderBy,
        orderDir: f.orderDir,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: GetAllReasonsResponse) => {
          this.reasons.set(res.list || []);
          this.totalItems.set(res.total ?? this.reasons().length);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.errorGettingReasons.set(
            this.isArabic()
              ? err.error?.messageAr || 'حدث خطأ أثناء تحميل البيانات'
              : err.error?.messageEn || 'Error loading reasons'
          );
          this.cdr.markForCheck();
        },
      });
  }

  applyFilters(): void {
    this.appliedFilters.set({
      reason: this.reasonFilter(),
      createdOnFrom: this.createdOnFrom(),
      createdOnTo: this.createdOnTo(),
      modifiedOnFrom: this.modifiedOnFrom(),
      modifiedOnTo: this.modifiedOnTo(),
      orderBy: this.orderBy(),
      orderDir: this.orderDir(),
    });
    this.currentPage.set(1);
    this.loadReasons();
  }

  resetFilters(): void {
    this.reasonFilter.set('');
    this.createdOnFrom.set('');
    this.createdOnTo.set('');
    this.modifiedOnFrom.set('');
    this.modifiedOnTo.set('');
    this.size.set(25);
    this.orderBy.set('CREATION_DATE');
    this.orderDir.set('DESC');

    this.itemsPerPage.set(25);
    this.appliedFilters.set({
      reason: '',
      createdOnFrom: '',
      createdOnTo: '',
      modifiedOnFrom: '',
      modifiedOnTo: '',
      orderBy: 'CREATION_DATE',
      orderDir: 'DESC',
    });
    this.currentPage.set(1);
    this.loadReasons();
  }

  toggleSortOrder(): void {
    this.orderDir.update((value) => (value === 'ASC' ? 'DESC' : 'ASC'));
    this.currentPage.set(1);
    this.applyFilters();
  }

  onPageSizeChange(newSize: number): void {
    this.itemsPerPage.set(newSize);
    this.size.set(newSize);
    this.currentPage.set(1);
    this.loadReasons();
  }

  // ================= Pagination Calculations =================
  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()) || 1);

  startItem = computed(() =>
    this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1
  );

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages() && page !== this.currentPage()) {
      this.currentPage.set(page);
      this.loadReasons();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
      this.loadReasons();
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      this.loadReasons();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalPages(); i++) {
      pages.push(i);
    }
    return pages;
  }

  // ================= Navigation & Dialog Actions =================
  onAddReason(): void {
    this.router.navigate(['create'], { relativeTo: this.route });
  }

  onEditReason(reason: UndeliveredReason): void {
    this.router.navigate(['edit', reason.id], { relativeTo: this.route });
  }

  onDeleteReason(reason: UndeliveredReason): void {
    this.reasonToDelete.set(reason);
    this.showDeleteDialog.set(true);
    this.cdr.markForCheck();
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog.set(false);
    this.reasonToDelete.set(null);
    this.cdr.markForCheck();
  }

  onDeleteSuccess(message: string): void {
    this.closeDeleteDialog();
    this.successToastMessage.set(message);
    this.showSuccessMessage.set(true);
    this.cdr.markForCheck();

    setTimeout(() => {
      this.showSuccessMessage.set(false);
      this.cdr.markForCheck();
    }, 3000);

    this.loadReasons();
  }
}